import { toast, type Id } from 'vue3-toastify';

/**
 * Validação dos campos editáveis de um usuário (Meu Perfil e Gerenciar Equipe) e
 * leitura do erro que a API devolve ao recusá-los.
 *
 * As regras espelham o `CreateUsuarioDto` do server (`@IsNotEmpty` no nome,
 * `@IsEmail` no e-mail, `@IsStrongPassword` na senha): o front barra antes de
 * enviar, e o que escapar volta da API em `campos`, com a mesma mensagem.
 */

export type CampoUsuario = 'nome' | 'email' | 'senha';
export type ErrosDeCampo = Partial<Record<CampoUsuario, string>>;

const CAMPOS: CampoUsuario[] = ['nome', 'email', 'senha'];

// Mesmo conjunto de símbolos que o `isStrongPassword` do validator.js (usado pelo
// class-validator) reconhece — um caractere fora dele não conta como símbolo lá.
const SIMBOLO = /[-#!$@£%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validarNome(nome: string): string | undefined {
    if (!nome.trim()) return 'O nome é obrigatório.';
}

export function validarEmail(email: string): string | undefined {
    if (!email.trim()) return 'O e-mail é obrigatório.';
    if (!EMAIL.test(email.trim())) return 'Informe um e-mail válido (ex.: nome@empresa.com).';
}

/** Um item da regra de senha do server (`@IsStrongPassword`), para o checklist do formulário. */
export interface RegraSenha {
    chave: 'tamanho' | 'minuscula' | 'maiuscula' | 'numero' | 'simbolo';
    texto: string;
    ok: boolean;
}

/**
 * A regra do server item a item: mín. 8 caracteres, 1 minúscula, 1 maiúscula,
 * 1 número e 1 símbolo. Uma senha só é aceita com TODOS os itens ok.
 */
export function regrasDaSenha(senha: string): RegraSenha[] {
    const s = senha ?? '';
    return [
        { chave: 'tamanho', texto: 'Mínimo de 8 caracteres', ok: s.length >= 8 },
        { chave: 'minuscula', texto: 'Uma letra minúscula', ok: /[a-z]/.test(s) },
        { chave: 'maiuscula', texto: 'Uma letra maiúscula', ok: /[A-Z]/.test(s) },
        { chave: 'numero', texto: 'Um número', ok: /[0-9]/.test(s) },
        { chave: 'simbolo', texto: 'Um símbolo (ex.: ! @ # $ %)', ok: SIMBOLO.test(s) },
    ];
}

/** `true` só quando a senha cumpre a regra inteira do server. */
export function senhaAtendeRegra(senha: string): boolean {
    return regrasDaSenha(senha).every((r) => r.ok);
}

/** Senha vazia = "não trocar a senha", então não é erro. */
export function validarSenha(senha: string): string | undefined {
    if (!senha) return;
    const faltando: string[] = [];
    if (senha.length < 8) faltando.push('8 caracteres');
    if (!/[a-z]/.test(senha)) faltando.push('uma letra minúscula');
    if (!/[A-Z]/.test(senha)) faltando.push('uma letra maiúscula');
    if (!/[0-9]/.test(senha)) faltando.push('um número');
    if (!SIMBOLO.test(senha)) faltando.push('um símbolo');
    if (faltando.length) return `A senha precisa ter no mínimo: ${faltando.join(', ')}.`;
}

/** Valida só os campos alterados — os demais não vão para a API. */
export function validarCamposAlterados(
    valores: { nome: string; email: string; senha?: string },
    alterados: Partial<Record<CampoUsuario, boolean>>,
): ErrosDeCampo {
    const erros: ErrosDeCampo = {};
    if (alterados.nome) erros.nome = validarNome(valores.nome);
    if (alterados.email) erros.email = validarEmail(valores.email);
    if (alterados.senha) erros.senha = validarSenha(valores.senha ?? '');
    for (const c of CAMPOS) if (!erros[c]) delete erros[c];
    return erros;
}

// Quando a API ainda não devolve `campos` (server de uma versão anterior), a
// mensagem plana é a junção das mensagens do DTO; reconhece-as pelo texto.
const MENSAGENS_CONHECIDAS: Array<[RegExp, CampoUsuario]> = [
    [/e-?mail (inválido|é obrigatório|já está em uso)|email em uso/i, 'email'],
    [/senha/i, 'senha'],
    [/o nome/i, 'nome'],
];

export interface ErroDaApi {
    mensagem: string;
    campos: ErrosDeCampo;
}

/**
 * Lê o erro lançado pelo `FetchUtil` (o corpo JSON da resposta, ou um `Error`).
 * O corpo do server é `{ statusCode, message, error?, campos? }`, com `campos`
 * no formato `{ email: ["E-mail inválido."] }`.
 */
export function lerErroDaApi(e: unknown, padrao = 'Erro ao salvar as alterações.'): ErroDaApi {
    const corpo = (e && typeof e === 'object' ? e : {}) as {
        message?: unknown;
        campos?: Record<string, unknown>;
    };
    const mensagem = Array.isArray(corpo.message)
        ? corpo.message.join(' ')
        : typeof corpo.message === 'string' && corpo.message.trim() ? corpo.message : padrao;

    const campos: ErrosDeCampo = {};
    if (corpo.campos && typeof corpo.campos === 'object') {
        for (const c of CAMPOS) {
            const v = corpo.campos[c];
            const msg = Array.isArray(v) ? v.filter(x => typeof x === 'string').join(' ') : typeof v === 'string' ? v : '';
            if (msg) campos[c] = msg;
        }
    } else if (mensagem !== padrao) {
        // A mensagem plana junta as do DTO com ", " — mas a da senha tem vírgulas
        // internas, então reparte só onde a próxima mensagem começa com maiúscula.
        for (const parte of mensagem.split(/,\s+(?=[A-ZÁÉÍÓÚ])/)) {
            const campo = MENSAGENS_CONHECIDAS.find(([re]) => re.test(parte))?.[1];
            if (campo) campos[campo] = campos[campo] ? `${campos[campo]} ${parte}` : parte;
        }
    }
    return { mensagem, campos };
}

let ultimoToastDeErro: Id | undefined;

/**
 * Toast de erro que NÃO some sozinho (o usuário fecha) — para a recusa da API ao
 * salvar, que precisa ser lida. Substitui o anterior para não empilhar repetidos.
 */
export function toastErroPersistente(mensagem: string) {
    if (ultimoToastDeErro !== undefined) toast.remove(ultimoToastDeErro);
    ultimoToastDeErro = toast.error(mensagem, { autoClose: false, closeOnClick: false });
}

/** Texto do toast: a mensagem de cada campo recusado, ou a mensagem geral. */
export function mensagemDoToast({ mensagem, campos }: ErroDaApi): string {
    const porCampo = CAMPOS.map(c => campos[c]).filter(Boolean);
    return `Não foi possível salvar: ${porCampo.length ? porCampo.join(' ') : mensagem}`;
}
